import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  LinearProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  PlayArrow as RunIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";
import { MonacoJsonEditor } from "../../components/MonacoJsonEditor";
import { EmailAutomationPipelineResultsList } from "./EmailAutomationPipelineResultsList";

interface PipelineConfig {
  id: string;
  name: string;
  description: string | null;
  categorizationStepConfig: Record<string, { prompt: string }>;
  propertyExtractionStepConfig: Record<
    string,
    { properties: Array<{ name: string; prompt: string }> }
  >;
  apiFetchStepConfig: Record<string, string[]>;
  presentationStepConfig: { promptTemplate: string };
  createdAt: string;
  updatedAt: string;
}

interface TestSet {
  id: string;
  name: string;
  emailCount: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

type PipelineStep = "categorizationStep" | "propertyExtractionStep" | "apiFetchStep" | "presentationStep";

const ALL_STEPS: PipelineStep[] = [
  "categorizationStep",
  "propertyExtractionStep",
  "apiFetchStep",
  "presentationStep",
];

const STEP_LABELS: Record<PipelineStep, string> = {
  categorizationStep: "1. Categorize",
  propertyExtractionStep: "2. Extract",
  apiFetchStep: "3. Fetch",
  presentationStep: "4. Present",
};

export function EmailAutomationPipelineConfigPage() {
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [config, setConfig] = useState<PipelineConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Editable fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryDefinitionsJson, setCategoryDefinitionsJson] = useState("");
  const [propertyDefinitionsJson, setPropertyDefinitionsJson] = useState("");
  const [apiCallsJson, setApiCallsJson] = useState("");
  const [presentationPromptTemplate, setPresentationPromptTemplate] = useState("");

  // Run dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [selectedTestSetId, setSelectedTestSetId] = useState<string>("");
  const [emailIds, setEmailIds] = useState("");
  const [stepsToRun, setStepsToRun] = useState<PipelineStep[]>(ALL_STEPS);
  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState<{
    resultId: string;
    workflowId: string;
    processedCount: number;
    totalCount: number;
    status: string;
  } | null>(null);
  const [resultsRefreshTrigger, setResultsRefreshTrigger] = useState(0);

  useEffect(() => {
    if (configId) {
      loadConfig();
    }
  }, [configId]);

  // Poll for progress when running
  useEffect(() => {
    if (!runProgress || runProgress.status === "completed" || runProgress.status === "failed") {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const res = await apiClient.post<{ result: any }>(
          `/admin/email-automation/results/get`,
          { id: runProgress.resultId }
        );
        const result = res.result;

        setRunProgress(prev => prev ? {
          ...prev,
          processedCount: result.processedCount,
          totalCount: result.totalCount,
          status: result.workflowStatus,
        } : null);

        if (result.workflowStatus === "completed" || result.workflowStatus === "failed") {
          clearInterval(pollInterval);
          if (result.workflowStatus === "completed") {
            showSnackbar(`Pipeline completed: ${result.processedCount} email(s) processed`, 4000, "success");
            // Refresh results list
            setResultsRefreshTrigger((prev) => prev + 1);
            // Navigate to results page
            navigate(`/admin/email-automation/result/${runProgress.resultId}`);
          } else {
            showSnackbar(`Pipeline failed: ${result.errorMessage || 'Unknown error'}`, 4000, "error");
          }
          setRunning(false);
          setRunDialogOpen(false);
          setRunProgress(null);
        }
      } catch (error) {
        console.error("Error polling progress:", error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [runProgress, navigate, showSnackbar]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ config: PipelineConfig }>(
        `/admin/email-automation/config/get`,
        { id: configId }
      );
      setConfig(res.config);
      setName(res.config.name);
      setDescription(res.config.description || "");
      setCategoryDefinitionsJson(
        JSON.stringify(res.config.categorizationStepConfig, null, 2)
      );
      setPropertyDefinitionsJson(
        JSON.stringify(res.config.propertyExtractionStepConfig, null, 2)
      );
      setApiCallsJson(
        JSON.stringify(res.config.apiFetchStepConfig, null, 2)
      );
      setPresentationPromptTemplate(res.config.presentationStepConfig.promptTemplate || "");
    } catch (error) {
      console.error("Error loading config:", error);
      setError("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!configId) return;

    // Validate JSON fields
    let categoryDefinitions, propertyDefinitions, apiCalls;
    try {
      categoryDefinitions = JSON.parse(categoryDefinitionsJson);
    } catch {
      setError("Invalid JSON in Category Definitions");
      setTabValue(1);
      return;
    }
    try {
      propertyDefinitions = JSON.parse(propertyDefinitionsJson);
    } catch {
      setError("Invalid JSON in Property Extraction Definitions");
      setTabValue(2);
      return;
    }
    try {
      apiCalls = JSON.parse(apiCallsJson);
    } catch {
      setError("Invalid JSON in API Calls Configuration");
      setTabValue(3);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await apiClient.put(`/admin/email-automation/config/update`, {
        id: configId,
        name: name.trim(),
        description: description.trim() || undefined,
        categorizationStepConfig: categoryDefinitions,
        propertyExtractionStepConfig: propertyDefinitions,
        apiFetchStepConfig: apiCalls,
        presentationStepConfig: { promptTemplate: presentationPromptTemplate },
      });
      showSnackbar("Configuration saved successfully", 3000, "success");
      loadConfig();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to save configuration";
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const loadTestSets = async () => {
    try {
      const res = await apiClient.post<{ testSets: TestSet[] }>(
        `/admin/email-test-sets/list`,
        {}
      );
      setTestSets(res.testSets);
    } catch (error) {
      console.error("Error loading test sets:", error);
    }
  };

  const handleOpenRunDialog = () => {
    setRunDialogOpen(true);
    loadTestSets();
    setSelectedTestSetId("");
    setEmailIds("");
    setStepsToRun(ALL_STEPS);
    setRunProgress(null);
  };

  const handleRunPipeline = async () => {
    if (!configId) return;

    const emailIdList = emailIds
      .split(/[,\s]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (emailIdList.length === 0 && !selectedTestSetId) {
      showSnackbar(
        "Please enter email IDs or select a test set",
        3000,
        "info"
      );
      return;
    }

    setRunning(true);
    try {
      const res = await apiClient.post<{ resultId: string; workflowId: string }>(
        `/admin/email-automation/run`,
        {
          configId,
          emailIds: emailIdList,
          testSetId: selectedTestSetId || undefined,
          stepsToRun: stepsToRun.length === 4 ? undefined : stepsToRun,
        }
      );

      // Set initial progress state to start polling
      setRunProgress({
        resultId: res.resultId,
        workflowId: res.workflowId,
        processedCount: 0,
        totalCount: emailIdList.length || 0,
        status: "pending",
      });

      showSnackbar("Pipeline started! Processing emails...", 3000, "info");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to start pipeline";
      showSnackbar(errorMessage, 4000, "error");
      setRunning(false);
    }
  };

  const handleStepToggle = (step: PipelineStep) => {
    setStepsToRun((prev) => {
      if (prev.includes(step)) {
        // When removing a step, also remove all subsequent steps
        const stepIndex = ALL_STEPS.indexOf(step);
        return prev.filter((s) => {
          const sIndex = ALL_STEPS.indexOf(s);
          return sIndex < stepIndex;
        });
      } else {
        // When adding a step, also add all previous steps
        const stepIndex = ALL_STEPS.indexOf(step);
        return ALL_STEPS.filter((_, i) => i <= stepIndex);
      }
    });
  };

  if (loading) {
    return (
      <AdminPage title="Edit Configuration">
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  if (!config) {
    return (
      <AdminPage title="Edit Configuration">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Configuration not found</Alert>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/email-automation")}
            sx={{ mt: 2 }}
          >
            Back to List
          </Button>
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage title={`Edit: ${config.name}`}>
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/email-automation")}
          >
            Back to List
          </Button>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RunIcon />}
              onClick={handleOpenRunDialog}
            >
              Run Pipeline
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Basic Info" />
            <Tab label="Categories" />
            <Tab label="Property Extraction" />
            <Tab label="API Calls" />
            <Tab label="Presentation" />
          </Tabs>

          {/* Basic Info Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Categories Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Define email categories. Each key is a category name, with a
              "prompt" field describing when to apply the category.
            </Typography>
            <MonacoJsonEditor
              value={categoryDefinitionsJson}
              onChange={setCategoryDefinitionsJson}
              height={500}
            />
          </TabPanel>

          {/* Property Extraction Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Define properties to extract per category. Each category can have
              multiple properties with their extraction prompts.
            </Typography>
            <MonacoJsonEditor
              value={propertyDefinitionsJson}
              onChange={setPropertyDefinitionsJson}
              height={500}
            />
          </TabPanel>

          {/* API Calls Tab */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure which API calls to execute per category. Each category
              maps to an array of registered API handler names.
            </Typography>
            <MonacoJsonEditor
              value={apiCallsJson}
              onChange={setApiCallsJson}
              height={500}
            />
          </TabPanel>

          {/* Presentation Tab */}
          <TabPanel value={tabValue} index={4}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Define the presentation prompt template. Use $variable syntax to
              inject extracted properties and fetched data (e.g., $categories,
              $etd, $container_number).
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={20}
              value={presentationPromptTemplate}
              onChange={(e) => setPresentationPromptTemplate(e.target.value)}
              sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
              placeholder="Generate a summary using $categories and $property_name..."
            />
          </TabPanel>
        </Paper>

        {/* Results List */}
        <EmailAutomationPipelineResultsList
          configId={configId}
          refreshTrigger={resultsRefreshTrigger}
        />

        {/* Run Pipeline Dialog */}
        <Dialog
          open={runDialogOpen}
          onClose={() => !running && setRunDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Run Pipeline</DialogTitle>
          <DialogContent>
            {runProgress ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Processing emails...
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={runProgress.totalCount > 0
                        ? (runProgress.processedCount / runProgress.totalCount) * 100
                        : 0
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {runProgress.processedCount} / {runProgress.totalCount}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Status: {runProgress.status}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Email Source
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Test Set</InputLabel>
                  <Select
                    value={selectedTestSetId}
                    label="Select Test Set"
                    onChange={(e) => setSelectedTestSetId(e.target.value)}
                  >
                    <MenuItem value="">-- None --</MenuItem>
                    {testSets.map((ts) => (
                      <MenuItem key={ts.id} value={ts.id}>
                        {ts.name} ({ts.emailCount} emails)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Or enter email IDs directly (comma or newline separated):
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={emailIds}
                  onChange={(e) => setEmailIds(e.target.value)}
                  placeholder="Enter email IDs..."
                  disabled={!!selectedTestSetId}
                />

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                  Steps to Run
                </Typography>
                <FormGroup row>
                  {ALL_STEPS.map((step, index) => (
                    <FormControlLabel
                      key={step}
                      control={
                        <Checkbox
                          checked={stepsToRun.includes(step)}
                          onChange={() => handleStepToggle(step)}
                          disabled={
                            index > 0 && !stepsToRun.includes(ALL_STEPS[index - 1]) &&
                            step !== "presentationStep"
                          }
                        />
                      }
                      label={STEP_LABELS[step]}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRunDialogOpen(false)} disabled={running}>
              {running ? "Running..." : "Cancel"}
            </Button>
            {!runProgress && (
              <Button
                variant="contained"
                onClick={handleRunPipeline}
                disabled={running || (!emailIds.trim() && !selectedTestSetId)}
                startIcon={
                  running ? <CircularProgress size={16} /> : <RunIcon />
                }
              >
                Run Pipeline
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
